class CartZipValidator {
  constructor() {
    this.storageKey = 'cartValidZipCode';
    this.init();
  }

  init() {
    this.validator = document.querySelector('.cart-zip-validator');
    if (!this.validator) {
      // Element doesn't exist yet, will be initialized later
      return;
    }

    this.input = this.validator.querySelector('.cart-zip-validator__input');
    this.button = this.validator.querySelector('.cart-zip-validator__button');
    this.message = this.validator.querySelector('.cart-zip-validator__message');
    
    // Find checkout button (may not exist yet in drawer)
    this.updateCheckoutButtonReference();
    
    this.bindEvents();
    this.checkInitialState();
  }

  updateCheckoutButtonReference() {
    this.checkoutButton = document.querySelector('#CartDrawer-Checkout, .cart__checkout-button');
  }

  bindEvents() {
    // Remove existing event listeners to prevent duplicates
    if (this.button) {
      this.button.removeEventListener('click', this.validateZipCode);
    }
    if (this.input) {
      this.input.removeEventListener('keydown', this.handleKeyDown);
      this.input.removeEventListener('input', this.handleInput);
    }

    // Create bound methods for event listeners
    this.validateZipCode = this.validateZipCode.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleInput = this.handleInput.bind(this);

    // Button click validation
    if (this.button) {
      this.button.addEventListener('click', this.validateZipCode);
    }
    
    // Enter key validation
    if (this.input) {
      this.input.addEventListener('keydown', this.handleKeyDown);
      this.input.addEventListener('input', this.handleInput);
    }

    // Listen for cart updates to re-check checkout button state
    document.addEventListener('cart:updated', () => {
      this.updateCheckoutButtonState();
    });

    // PubSub cart update listener
    if (typeof subscribe !== 'undefined') {
      subscribe('cartUpdate', () => {
        this.updateCheckoutButtonState();
      });
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Enter') {
      this.validateZipCode(e);
    }
  }

  handleInput(e) {
    const value = e.target.value.replace(/\D/g, '');
    const limitedValue = value.slice(0, 5);
    
    // Only update if the value actually changed to prevent infinite loops
    if (e.target.value !== limitedValue) {
      e.target.value = limitedValue;
    }

    // Clear any previous error states when user starts typing
    this.clearMessage();
    this.input.classList.remove('error');
  }

  checkInitialState() {
    const savedZip = this.getSavedZipCode();
    if (savedZip && this.isZipCodeValid(savedZip)) {
      // Set the input value and show success state
      this.input.value = savedZip;
      this.showSuccessMessage();
      this.enableCheckoutButton();
      // Dispatch event to notify other components about existing valid zip code
      document.dispatchEvent(new CustomEvent('zipcode:validated', {
        detail: { zipCode: savedZip }
      }));
    } else {
      // Clear any invalid saved zip code
      if (savedZip && !this.isZipCodeValid(savedZip)) {
        this.clearSavedZipCode();
      }
      this.disableCheckoutButton();
    }
  }

  validateZipCode(e) {
    e.preventDefault();
    const zipCode = this.input.value.trim();

    // Check if field is empty
    if (!zipCode) {
      this.showErrorMessage('Please enter a zip code.');
      this.input.focus();
      return;
    }

    // Check if zip code is exactly 5 digits
    if (zipCode.length !== 5) {
      this.showErrorMessage('Please enter a valid 5-digit zip code.');
      this.input.focus();
      return;
    }

    // Check delivery zones
    if (!window.deliveryZones) {
      console.error('Delivery zones data is not available');
      this.showErrorMessage('Unable to verify zip code. Please try again.');
      return;
    }

    // Validate zip code against delivery zones
    const isValid = this.isZipCodeValid(zipCode);

    if (isValid) {
      this.saveZipCode(zipCode);
      this.showSuccessMessage();
      this.enableCheckoutButton();
    } else {
      this.clearSavedZipCode(false);
      this.showErrorMessage('Stocked is not available in your area.');
      this.disableCheckoutButton();
    }
  }

  isZipCodeValid(zipCode) {
    // Check if delivery zones are available
    if (!window.deliveryZones || !Array.isArray(window.deliveryZones)) {
      console.warn('Delivery zones data is not available for validation');
      return false;
    }

    const zones = window.deliveryZones;
    for (const zone of zones) {
      if (zone.zip_codes && Array.isArray(zone.zip_codes) && zone.zip_codes.includes(zipCode)) {
        return true;
      }
    }
    return false;
  }

  saveZipCode(zipCode) {
    try {
      localStorage.setItem(this.storageKey, zipCode);
      // Dispatch event to notify other components about zip code validation
      document.dispatchEvent(new CustomEvent('zipcode:validated', {
        detail: { zipCode: zipCode }
      }));
    } catch (error) {
      console.warn('Could not save zip code to localStorage:', error);
    }
  }

  getSavedZipCode() {
    try {
      return localStorage.getItem(this.storageKey);
    } catch (error) {
      console.warn('Could not read zip code from localStorage:', error);
      return null;
    }
  }

  showSuccessMessage() {
    this.message.textContent = 'Stocked is available in your area.';
    this.message.className = 'cart-zip-validator__message success';
    this.input.classList.remove('error');
  }

  showErrorMessage(text) {
    this.message.textContent = text;
    this.message.className = 'cart-zip-validator__message error';
    this.input.classList.add('error');
  }

  clearMessage() {
    this.message.textContent = '';
    this.message.className = 'cart-zip-validator__message';
  }

  enableCheckoutButton() {
    this.updateCheckoutButtonReference();
    if (this.checkoutButton) {
      this.checkoutButton.disabled = false;
      this.checkoutButton.setAttribute('aria-disabled', 'false');
    }
  }

  disableCheckoutButton() {
    this.updateCheckoutButtonReference();
    if (this.checkoutButton) {
      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute('aria-disabled', 'true');
    }
  }

  updateCheckoutButtonState() {
    // Always update the checkout button reference before checking state
    this.updateCheckoutButtonReference();
    
    const savedZip = this.getSavedZipCode();
    if (savedZip && this.isZipCodeValid(savedZip)) {
      this.enableCheckoutButton();
    } else {
      this.disableCheckoutButton();
    }
  }

  // Public method to clear saved zip code (for use by other components)
  clearSavedZipCode(clearInput = true) {
    try {
      localStorage.removeItem(this.storageKey);
      if (clearInput && this.input) {
        this.input.value = '';
        this.clearMessage();
      }
      this.disableCheckoutButton();
      // Dispatch event to notify other components about zip code clearing
      document.dispatchEvent(new CustomEvent('zipcode:cleared'));
    } catch (error) {
      console.warn('Could not clear zip code from localStorage:', error);
    }
  }

  // Public method to populate input field with saved zip code
  populateFromStorage() {
    const savedZip = this.getSavedZipCode();
    if (savedZip && this.input) {
      this.input.value = savedZip;
      if (this.isZipCodeValid(savedZip)) {
        this.showSuccessMessage();
        this.enableCheckoutButton();
        // Dispatch event to notify other components about valid zip code
        document.dispatchEvent(new CustomEvent('zipcode:validated', {
          detail: { zipCode: savedZip }
        }));
      } else {
        // Clear invalid zip code
        this.clearSavedZipCode();
      }
    }
  }

  // Public method to reinitialize validator with new DOM elements
  reinitialize() {
    // Only reinitialize if the current DOM elements are stale or missing
    const currentValidator = document.querySelector('.cart-zip-validator');
    if (!currentValidator || this.validator !== currentValidator) {
      this.init();
    }
  }

  // Check if the validator is properly bound to current DOM elements
  isProperlyBound() {
    const currentValidator = document.querySelector('.cart-zip-validator');
    return currentValidator && 
           this.validator === currentValidator && 
           this.input && 
           this.button && 
           this.message &&
           this.input.parentNode &&  // Ensure elements are still in DOM
           this.button.parentNode && 
           this.message.parentNode;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initializeZipValidator();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  initializeZipValidator();
}

// Re-initialize when cart content is dynamically updated
document.addEventListener('cart:updated', () => {
  // Small delay to ensure DOM has been updated
  setTimeout(() => {
    initializeZipValidator();
  }, 100);
});

// Also listen for cart drawer opening specifically
document.addEventListener('click', (event) => {
  const cartIcon = event.target.closest('#cart-icon-bubble');
  if (cartIcon) {
    // Delay to allow cart drawer to open and render
    setTimeout(() => {
      initializeZipValidator();
    }, 200);
  }
});

// Watch for cart drawer becoming active via mutation observer
const observeCartDrawer = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('active') && document.querySelector('.cart-zip-validator')) {
            // Cart drawer became active, initialize zip validator
            setTimeout(() => {
              window.cartZipValidator = new CartZipValidator();
            }, 100);
          }
        }
      });
    });
    
    observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }
};

// Initialize observer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartDrawer);
} else {
  observeCartDrawer();
}

// PubSub cart update listener - be more aggressive for product form submissions
if (typeof subscribe !== 'undefined') {
  subscribe('cartUpdate', (event) => {
    // Always reinitialize for product-form events (when items are added to cart)
    if (event.source === 'product-form') {
      // Multiple attempts with different delays to ensure we catch the cart drawer opening
      setTimeout(() => {
        if (document.querySelector('.cart-zip-validator')) {
          window.cartZipValidator = new CartZipValidator();
        }
      }, 100);
      
      setTimeout(() => {
        if (document.querySelector('.cart-zip-validator')) {
          window.cartZipValidator = new CartZipValidator();
        }
      }, 300);
      
      setTimeout(() => {
        if (document.querySelector('.cart-zip-validator')) {
          window.cartZipValidator = new CartZipValidator();
        }
      }, 500);
    } else if (event.source === 'cart-drawer' || (event.cartData && event.cartData.item_count === 0)) {
      // Use smart initialization for other sources
      setTimeout(() => {
        initializeZipValidator();
      }, 100);
    }
  });
}

// Helper function to initialize or re-initialize the zip validator
function initializeZipValidator() {
  const validatorElement = document.querySelector('.cart-zip-validator');
  
  if (validatorElement) {
    if (window.cartZipValidator) {
      // Only reinitialize if the validator is not properly bound to current DOM
      if (typeof window.cartZipValidator.isProperlyBound === 'function' && 
          !window.cartZipValidator.isProperlyBound()) {
        window.cartZipValidator.reinitialize();
      }
    } else {
      // Create new instance if none exists
      window.cartZipValidator = new CartZipValidator();
    }
  }
}
